<template>
  <div>
    <!-- Header -->
    <div class="flex flex-col-xs-only flex-wrap justify-space-between align-items-center p-y-m p-l-sm-xs-only p-r-n-xs-only">
      <h1 class="font-heavy m-t-none m-b-none">Division Standards</h1>
      <div class="flex gap-xs align-self-flex-start-xs-only m-t-sm-xs-only">
        <button v-if="customStandards.length > 0" class="btn btn-default btn-compact" @click="scrollToCustom">
          <i class="fa-solid fa-arrow-down m-r-xxs"></i> Custom Standards
        </button>
        <a :href="newUrl" class="btn btn-primary btn-compact">
          <i class="fa-solid fa-plus m-r-xxs"></i> Create Custom Standard
        </a>
      </div>
    </div>

    <!-- Search -->
    <div class="m-b-sm">
      <div class="input-group">
        <span class="input-group-addon"><i class="fa-solid fa-magnifying-glass"></i></span>
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="Search standards by name..."
        >
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">
      {{ errorMessage }}
    </div>

    <div v-if="filteredStandards.length === 0 && searchQuery" class="alert alert-info">
      No standards match "{{ searchQuery }}".
    </div>

    <div v-if="allStandards.length === 0" class="alert alert-info">
      No division standards available. Create a custom standard or contact support to seed system standards.
    </div>

    <!-- System Standards -->
    <div v-if="filteredSystemStandards.length > 0" class="m-b-lg">
      <h3 class="text-18 font-semi m-b-sm">System Standards</h3>
      <div class="flex flex-col gap-sm">
        <div v-for="standard in filteredSystemStandards" :key="standard.id" class="border border-solid border-gray-200 rounded-xs p-sm">
          <h4 class="m-b-none font-semi">{{ standard.name }}</h4>
          <div class="text-14 text-gray-600">
            {{ standard.sanctioning_body_full_name }}
          </div>
          <div class="text-14 text-gray-600 m-t-xxs">
            {{ standard.age_divisions.length }} division{{ standard.age_divisions.length === 1 ? '' : 's' }}
            <span v-if="standard.age_divisions.length > 0">
              ({{ divisionSummary(standard) }})
            </span>
          </div>

          <!-- Expandable division details -->
          <div v-if="expanded[standard.id]" class="m-t-sm flex flex-col gap-xs">
            <div v-for="division in standard.age_divisions" :key="division.id" class="bg-gray-50 rounded-xs p-sm">
              <div class="font-semi text-14">
                {{ division.display_name }}
                <span v-if="division.eligibility_summary" class="font-normal text-gray-600">
                  &middot; {{ division.eligibility_summary }}
                </span>
              </div>
              <div v-if="division.eligibility_note" class="text-12 text-gray-500 m-t-xxs">
                {{ division.eligibility_note }}
              </div>
              <div v-if="division.weight_classes && division.weight_classes.length > 0" class="text-12 text-gray-700 m-t-xxs">
                {{ division.weight_classes.map(w => w.display_name).join(', ') }}
              </div>
            </div>
          </div>

          <button
            v-if="standard.age_divisions.length > 0"
            class="btn btn-xs btn-link p-l-none m-t-xs"
            @click="toggleExpanded(standard.id)"
          >
            <i :class="expanded[standard.id] ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="m-r-xxs"></i>
            {{ expanded[standard.id] ? 'Hide details' : 'Show details' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Custom Standards -->
    <div ref="customSection">
      <h3 class="text-18 font-semi m-b-sm">Your Custom Standards</h3>

      <div v-if="filteredCustomStandards.length === 0 && !searchQuery" class="text-gray-500 p-sm border border-solid border-gray-200 rounded-xs bg-gray-50">
        No custom standards yet. Click "Create Custom Standard" to define your own age divisions and weight classes.
      </div>

      <div v-else-if="filteredCustomStandards.length === 0 && searchQuery" class="text-gray-500 p-sm border border-solid border-gray-200 rounded-xs bg-gray-50">
        No custom standards match "{{ searchQuery }}".
      </div>

      <div v-else class="flex flex-col gap-sm">
        <div v-for="standard in filteredCustomStandards" :key="standard.id" class="border border-solid border-gray-200 rounded-xs p-sm">
          <div class="flex justify-space-between align-items-start m-b-sm">
            <div>
              <h4 class="m-b-none font-semi">{{ standard.name }}</h4>
              <div class="text-14 text-gray-600 m-t-xxs">
                {{ standard.age_divisions.length }} division{{ standard.age_divisions.length === 1 ? '' : 's' }}
                <span v-if="standard.age_divisions.length > 0">
                  ({{ divisionSummary(standard) }})
                </span>
              </div>
            </div>
            <div class="flex gap-xs">
              <a :href="editUrl(standard)" class="btn btn-sm btn-default">
                <i class="fa-solid fa-pencil"></i> Edit
              </a>
              <button class="btn btn-sm btn-danger" @click="confirmDelete(standard)">
                <i class="fa-solid fa-trash-can"></i>
              </button>
            </div>
          </div>

          <!-- Expandable division details -->
          <div v-if="expanded[standard.id]" class="m-t-sm flex flex-col gap-xs">
            <div v-for="division in standard.age_divisions" :key="division.id" class="bg-gray-50 rounded-xs p-sm">
              <div class="font-semi text-14">
                {{ division.display_name }}
                <span v-if="division.eligibility_summary" class="font-normal text-gray-600">
                  &middot; {{ division.eligibility_summary }}
                </span>
              </div>
              <div v-if="division.eligibility_note" class="text-12 text-gray-500 m-t-xxs">
                {{ division.eligibility_note }}
              </div>
              <div v-if="division.weight_classes && division.weight_classes.length > 0" class="text-12 text-gray-700 m-t-xxs">
                {{ division.weight_classes.map(w => w.display_name).join(', ') }}
              </div>
            </div>
          </div>

          <button
            v-if="standard.age_divisions.length > 0"
            class="btn btn-xs btn-link p-l-none m-t-xs"
            @click="toggleExpanded(standard.id)"
          >
            <i :class="expanded[standard.id] ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="m-r-xxs"></i>
            {{ expanded[standard.id] ? 'Hide details' : 'Show details' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal fade in" style="display: block;" @click.self="cancelDelete">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <button type="button" class="close" @click="cancelDelete">&times;</button>
            <h4 class="modal-title">Delete Standard</h4>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to delete <strong>{{ standardToDelete ? standardToDelete.name : '' }}</strong>?</p>
            <p class="text-gray-600">This action cannot be undone. Any team registrations using this standard will need to be updated.</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-default" @click="cancelDelete">Cancel</button>
            <ladda-button @lbClicked="deleteStandard" el-class="btn-danger" :loading="deleting">
              Delete
            </ladda-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showDeleteConfirm" class="modal-backdrop fade in"></div>
  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import { errorableMix } from './mixins/errorable_mix';

export default {
  name: 'division-standards-list',
  components: { LaddaButton },
  mixins: [errorableMix],
  props: {
    standards: {
      type: Object,
      required: true,
    },
    newUrl: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      allStandards: [],
      expanded: {},
      searchQuery: '',
      showDeleteConfirm: false,
      standardToDelete: null,
      deleting: false,
    };
  },
  computed: {
    systemStandards() {
      return this.allStandards.filter((s) => !s.editable);
    },
    customStandards() {
      return this.allStandards.filter((s) => s.editable);
    },
    filteredStandards() {
      if (!this.searchQuery) return this.allStandards;
      const query = this.searchQuery.toLowerCase();
      return this.allStandards.filter((s) => s.name.toLowerCase().includes(query));
    },
    filteredSystemStandards() {
      return this.filteredStandards.filter((s) => !s.editable);
    },
    filteredCustomStandards() {
      return this.filteredStandards.filter((s) => s.editable);
    },
  },
  mounted() {
    if (this.standards && this.standards.division_standards) {
      this.allStandards = this.standards.division_standards;
    }
  },
  methods: {
    divisionSummary(standard) {
      const names = standard.age_divisions.slice(0, 3).map((d) => d.name);
      if (standard.age_divisions.length > 3) {
        return `${names.join(', ')}...`;
      }
      return names.join(', ');
    },
    editUrl(standard) {
      return `/settings/team/division_standards/${standard.id}/edit`;
    },
    toggleExpanded(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },
    scrollToCustom() {
      this.$refs.customSection.scrollIntoView({ behavior: 'smooth' });
    },
    confirmDelete(standard) {
      this.standardToDelete = standard;
      this.showDeleteConfirm = true;
    },
    cancelDelete() {
      this.standardToDelete = null;
      this.showDeleteConfirm = false;
    },
    async deleteStandard() {
      if (!this.standardToDelete) return;

      this.deleting = true;

      try {
        await axios.delete(
          this.$apiService.divisionStandardUrl(this.standardToDelete.id),
        );

        const idx = this.allStandards.findIndex((s) => s.id === this.standardToDelete.id);
        if (idx > -1) {
          this.allStandards.splice(idx, 1);
        }

        this.cancelDelete();
      } catch (error) {
        this.showError(this.parseErrorResponse(error));
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>
